import { type ComponentProps, useMemo, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Button, { TooltipButton } from "@/v2-backoffice-client/components/button"
import { useTableContext } from "./table.context"
import { getColumnTitle } from "./table.utils"
import type { ColumnFilterConfig, FilterGroup } from "./table.types"
import TableAdvancedFilterGroup from "./filter/TableAdvancedFilterGroup"
import TableFilterPanelTabs from "./filter/TableFilterPanelTabs"
import {
  hasActiveFilter,
  newRootGroup,
  pruneGroup,
  type AdvancedFilterColumn,
} from "./filter/tableAdvancedFilter.utils"

export type TableAdvancedFilterPanelProps = ComponentProps<"div"> & {
  onClose(): void
}

/**
 * Builder for nested AND/OR/NOT filters. Edits a local draft tree and commits
 * it to the table's advanced-filter state (and thus the server query) on
 * Apply. Lives alongside the simple per-column filter panel.
 */
function TableAdvancedFilterPanel(props: TableAdvancedFilterPanelProps) {
  const { onClose, className, ...divProps } = props
  const { table, advancedFilter, setAdvancedFilter } = useTableContext()

  const columns: AdvancedFilterColumn[] = useMemo(() => {
    return table
      .getAllColumns()
      .map((column) => {
        if (column.id === "__select") return null
        const meta = column.columnDef.meta as
          | { filter?: ColumnFilterConfig }
          | undefined
        if (!meta?.filter) return null
        return {
          id: column.id,
          label: getColumnTitle(column),
          filter: meta.filter,
        }
      })
      .filter((c): c is AdvancedFilterColumn => c !== null)
  }, [table])

  const [draft, setDraft] = useState<FilterGroup>(
    () => advancedFilter ?? newRootGroup(columns),
  )

  function onApply() {
    setAdvancedFilter(pruneGroup(draft))
  }

  function onClear() {
    const fresh = newRootGroup(columns)
    setDraft(fresh)
    setAdvancedFilter(null)
  }

  const canApply = hasActiveFilter(draft) || advancedFilter !== null

  return (
    <div
      className={cn(
        "ui-table-toolbar-panel ui-table-adv-filter-panel",
        className,
      )}
      // Cmd/Ctrl+Enter applies from anywhere in the builder, so the operator
      // never has to reach for the button after editing a deeply nested rule.
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canApply) {
          e.preventDefault()
          onApply()
        }
      }}
      {...divProps}
    >
      <div className="ui-table-toolbar-panel-header">
        <TableFilterPanelTabs />
        <div className="ui-table-toolbar-panel-header-spacer" />
        <Button
          variant="ghost"
          size="xs"
          iconSrc="cross-small"
          iconClassName="ui-table-panel-close-icon"
          aria-label="Close the filter panel"
          onClick={onClose}
        />
      </div>

      <div className="ui-table-toolbar-panel-body">
        {columns.length === 0 ? (
          <p className="ui-table-adv-filter-empty">
            No filterable columns are configured for this table.
          </p>
        ) : (
          <>
            <p className="ui-table-adv-filter-help">
              Build a rule from conditions. Use <strong>all</strong> to require
              every condition or <strong>any</strong> to require just one, and
              add nested groups for more complex logic.
            </p>
            <TableAdvancedFilterGroup
              group={draft}
              columns={columns}
              depth={0}
              onChange={setDraft}
            />
          </>
        )}
      </div>

      <div className="ui-table-adv-filter-footer">
        <TooltipButton
          content="⌘/Ctrl+Enter"
          variant="primary"
          size="xs"
          onClick={onApply}
          disabled={!canApply}
        >
          Apply Filters
        </TooltipButton>
        <Button variant="ghost" size="xs" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  )
}

export default TableAdvancedFilterPanel
